/**
 * ============================================================================
 * СЕРВИС ДОМАШНИХ ЗАДАНИЙ
 * ============================================================================
 * Методы для работы с домашними заданиями
 * ============================================================================
 */

import api from './api'

export const homeworkService = {
	/**
	 * Получить все ДЗ модуля
	 * @param {number} moduleId - ID модуля
	 * @param {boolean} includeUnpublished - Включать ли неопубликованные ДЗ (по умолчанию true для преподавателей)
	 */
	async getByModuleId(moduleId, includeUnpublished = true) {
		const response = await api.get(`/modules/${moduleId}/homework`, {
			params: { includeUnpublished },
		})
		return response.data
	},

	/**
	 * Получить ДЗ по ID
	 */
	async getById(id) {
		const response = await api.get(`/homework/${id}`)
		return response.data
	},

	/**
	 * Создать новое ДЗ
	 */
	async create(moduleId, homeworkData) {
		const response = await api.post(
			`/modules/${moduleId}/homework`,
			homeworkData
		)
		return response.data
	},

	/**
	 * Обновить ДЗ
	 */
	async update(id, homeworkData) {
		const response = await api.put(`/homework/${id}`, homeworkData)
		return response.data
	},

	/**
	 * Удалить ДЗ
	 */
	async delete(id) {
		const response = await api.delete(`/homework/${id}`)
		return response.data
	},

	/**
	 * Загрузить материалы ДЗ
	 */
	async uploadMaterials(homeworkId, files) {
		const formData = new FormData()
		files.forEach(file => {
			formData.append('materials', file)
		})
		const response = await api.post(`/homework/${homeworkId}/materials`, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		})
		return response.data
	},

	/**
	 * Удалить материал ДЗ по file_path
	 */
	async deleteMaterial(homeworkId, filePath) {
		if (!filePath) {
			throw new Error('filePath is required')
		}
		const response = await api.delete(`/homework/${homeworkId}/materials`, {
			data: { file_path: filePath },
		})
		return response.data
	},

	/**
	 * Опубликовать ДЗ
	 */
	async publish(id) {
		const response = await api.post(`/homework/${id}/publish`)
		return response.data
	},

	/**
	 * Отправить решение ДЗ
	 */
	async submit(homeworkId, submissionData) {
		const formData = new FormData()
		formData.append('answer', submissionData?.answer ?? '')

		if (
			submissionData?.submission_text !== undefined ||
			submissionData?.submissionText !== undefined
		) {
			formData.append(
				'submission_text',
				submissionData?.submission_text ?? submissionData?.submissionText ?? ''
			)
		}

		if (submissionData?.existingFiles) {
			formData.append(
				'existing_files',
				JSON.stringify(submissionData.existingFiles)
			)
		}

		if (submissionData?.files) {
			submissionData.files.forEach(file => {
				formData.append('files', file)
			})
		}

		const response = await api.post(
			`/homework/${homeworkId}/submit`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)
		return response.data
	},

	/**
	 * Получить отправленные решения студента
	 */
	async getMySubmissions(homeworkId) {
		const response = await api.get(`/homework/${homeworkId}/submissions/my`)
		return response.data
	},

	/**
	 * Получить все отправленные решения (для преподавателя)
	 */
	async getAllSubmissions(homeworkId) {
		const response = await api.get(`/homework/${homeworkId}/submissions`)
		return response?.data ?? response
	},

	/**
	 * Оценить решение студента
	 */
	async gradeSubmission(submissionId, gradeData) {
		const response = await api.post(
			`/homework/submissions/${submissionId}/grade`,
			gradeData
		)
		return response?.data ?? response
	},

	/**
	 * Отправить на доработку (преподаватель)
	 */
	async requestRevision(submissionId, revisionData) {
		const formData = new FormData()
		formData.append(
			'revision_request_text',
			revisionData?.text ?? revisionData?.comment ?? ''
		)

		if (revisionData?.files) {
			revisionData.files.forEach(file => {
				formData.append('files', file)
			})
		}

		const response = await api.post(
			`/homework/submissions/${submissionId}/revision`,
			formData,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
			}
		)
		return response?.data ?? response
	},

	/**
	 * Отправить исправленное задание (студент)
	 */
	async submitRevision(homeworkId, revisionData) {
		const formData = new FormData()
		formData.append(
			'revision_submission_text',
			revisionData?.text ?? revisionData?.submissionText ?? ''
		)

		if (revisionData?.existingFiles) {
			formData.append(
				'revision_existing_files',
				JSON.stringify(revisionData.existingFiles)
			)
		}

		if (revisionData?.files) {
			revisionData.files.forEach(file => {
				formData.append('files', file)
			})
		}

		const response = await api.post(
			`/homework/${homeworkId}/submissions/revision`,
			formData,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
			}
		)
		return response?.data ?? response
	},

	async getSubmissions(homeworkId) {
		return await this.getAllSubmissions(homeworkId)
	},
}

export default homeworkService
